import React, { useState, useRef, useEffect } from "react";
import { Box, Button, Paper, Typography, styled, IconButton, CircularProgress } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Cropper from "react-easy-crop";
import { Area, Point } from "react-easy-crop/types";
import getCroppedImg from "./cropImage";
import EditIcon from "@mui/icons-material/Edit";

interface ProfilePictureUploadProps {
  onFileSelect: (file: File) => void;
  currentImage?: string;
  onUpload: () => Promise<boolean>;
  onDelete?: () => Promise<boolean>;
  loading?: boolean;
  disabled?: boolean;
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  onFileSelect,
  currentImage,
  onUpload,
  onDelete,
  loading = false,
  disabled = false,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(currentImage);
  const [hasNewFile, setHasNewFile] = useState<boolean>(false);
  const [cropping, setCropping] = useState<boolean>(false);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [tempImageUrl, setTempImageUrl] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update internal state whenever currentImage prop changes
  useEffect(() => {
    setPreviewUrl(currentImage);
    if (!currentImage) {
      setHasNewFile(false);
    }
  }, [currentImage]);

  const onCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const showCroppedImage = async () => {
    try {
      if (tempImageUrl && croppedAreaPixels) {
        const croppedImage = await getCroppedImg(tempImageUrl, croppedAreaPixels);
        onFileSelect(croppedImage as File);
        setPreviewUrl(URL.createObjectURL(croppedImage as Blob));
        setHasNewFile(true);
        setCropping(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      const objectUrl = URL.createObjectURL(selectedFile);
      setTempImageUrl(objectUrl);
      setCropping(true);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const droppedFile = files[0];
      const objectUrl = URL.createObjectURL(droppedFile);
      setTempImageUrl(objectUrl);
      setCropping(true);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClick = () => {
    if (disabled) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    if (hasNewFile) {
      onUpload();
    }
  }, [hasNewFile]);

  return (
    <Container>
      <input
        type="file"
        accept="image/jpeg,image/png"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleFileChange}
        disabled={disabled}
      />

      {cropping && tempImageUrl ? (
        // Cropping mode - show cropper
        <CropperSection>
          <CropperContainer>
            <Cropper
              image={tempImageUrl}
              crop={crop}
              cropShape="round"
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 2,
                justifyContent: "center",
                width: "100%",
                position: "absolute",
                bottom: 16,
                left: 0,
                zIndex: 1,
              }}
            >
              <CropperActionIconButton
                color="primary"
                onClick={showCroppedImage}
                title="Crop & Save"
                sx={{
                  backgroundColor: "black",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#333",
                  },
                }}
              >
                <CheckIcon />
              </CropperActionIconButton>
              <CropperActionIconButton
                color="error"
                onClick={() => {
                  setCropping(false);
                  setTempImageUrl(undefined);
                }}
                title="Cancel"
                sx={{
                  backgroundColor: "#f44336",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#d32f2f",
                  },
                }}
              >
                <CloseIcon />
              </CropperActionIconButton>
            </Box>
          </CropperContainer>
        </CropperSection>
      ) : previewUrl ? (
        // Preview mode - show image preview with actions
        <PreviewSection>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              width: "100%",
              height: "100%",
              position: "relative",
            }}
          >
            <ProfileImagePreview>
              <ProfileImage src={previewUrl} alt="Profile preview" />
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    borderRadius: "50%",
                  }}
                >
                  <CircularProgress sx={{ color: "white" }} />
                </Box>
              )}
            </ProfileImagePreview>

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <WhiteIconButton
                onClick={async () => {
                  if (hasNewFile && onDelete) {
                    setPreviewUrl(undefined);
                    setHasNewFile(false);
                  } else if (onDelete) {
                    const success = await onDelete();
                    if (success) {
                      setPreviewUrl(undefined);
                      setHasNewFile(false);
                    }
                  } else {
                    setPreviewUrl(undefined);
                    setHasNewFile(false);
                  }
                }}
                disabled={loading || disabled}
                title="Delete"
              >
                <DeleteIcon />
              </WhiteIconButton>

              {/* {hasNewFile && (
                <WhiteIconButton onClick={onUpload} disabled={loading} title="Upload">
                  <CheckIcon />
                </WhiteIconButton>
              )} */}
              <WhiteIconButton onClick={handleClick} disabled={loading || disabled} title="Change Image">
                <EditIcon />
              </WhiteIconButton>
            </Box>
          </Box>
        </PreviewSection>
      ) : (
        // Upload mode - show drag and drop
        <UploadSection onDrop={handleDrop} onDragOver={handleDragOver} onClick={handleClick}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Drop or upload
          </Typography>
          <UploadFileIcon sx={{ fontSize: 48, color: "black", mb: 2, fontWeight: "400" }} />

          <Typography variant="caption" color="primary" sx={{ mb: 3 }}>
            PNG OR JPEG FORMATS
          </Typography>
          <BrowseButton variant="contained">Browse files</BrowseButton>
        </UploadSection>
      )}
    </Container>
  );
};

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "20px",
  width: "100%",
  minHeight: "328px",
  maxHeight: "328px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const CropperSection = styled(Paper)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "#f5f5f5",
  minHeight: "328px",
  maxHeight: "328px",
  width: "100%",
  boxShadow: "none",
}));

const UploadSection = styled(Paper)(({ theme }) => ({
  flex: 1,
  display: "flex",
  boxShadow: "none",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  border: "2px dashed #ccc",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "#f2f2f2",
  cursor: "pointer",
  padding: 0,
  transition: "all 0.2s ease-in-out",
  minHeight: 300,
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: "#f8f8f8",
  },
}));

const PreviewSection = styled(Paper)(({ theme }) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  boxShadow: "none",
  justifyContent: "center",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "#999999 ",
  minHeight: 300,
}));

const ProfileImagePreview = styled(Box)({
  width: 225,
  height: 225,
  borderRadius: "50%",
  backgroundColor: "#e0e0e0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
  position: "relative",
});

const ProfileImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const BrowseButton = styled(Button)(() => ({
  backgroundColor: "black",
  color: "white",
  "&:hover": {
    backgroundColor: "#333",
  },
  borderRadius: "16px",
  padding: "8px 24px",
}));

const CropperContainer = styled(Box)({
  width: "100%",
  minHeight: "328px",
  maxHeight: "328px",
  position: "relative",
  backgroundColor: "#f5f5f5",
  borderRadius: "8px",
  overflow: "hidden",
});

const CropperActionIconButton = styled(IconButton)(() => ({
  borderRadius: "50%",
  padding: "12px",
  minWidth: "48px",
  minHeight: "48px",
  "&:hover": {
    transform: "scale(1.1)",
  },
  transition: "all 0.2s ease-in-out",
}));

const WhiteIconButton = styled(IconButton)(() => ({
  backgroundColor: "#fff",
  color: "#000",
  borderRadius: "50%",
  padding: "12px",
  minWidth: "48px",
  minHeight: "48px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  "&:hover": {
    backgroundColor: "#f0f0f0",
    transform: "scale(1.1)",
  },
  transition: "all 0.2s ease-in-out",
  "&:disabled": {
    backgroundColor: "#f0f0f0",
    color: "#ccc",
  },
}));

export default ProfilePictureUpload;
